import {Fragment, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {useFieldArray, useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMinus, faPlus} from "@fortawesome/free-solid-svg-icons";

// Components
import InputTime from "./InputTime.js";
import InputDate from "./InputDate.js";
import InputDay from "./InputDay.js";
import InputSelect from "./InputSelect.js";

// Style
import {pad} from "../../style/components/variables.js";
import {Button, FormFieldWrapper, Inline, Label, Text, Warning} from "../../style/components/general.js";

const options = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const InputNotification = ({name, type}) => {
  const {control, watch} = useFormContext();

  const {fields, remove, insert, append} = useFieldArray({control, name: `${name}.occurrences`});

  const daysOfMonth = useMemo(() => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(`${i}`);
    }
    return days;
  }, []);

  useEffect(() => {
    if (!fields?.length) {
      if (type === "annual") append({dayOfYear: null, time: null});
      if (type === "monthly") append({dayOfMonth: null, time: null});
      if (type === "weekly") append({dayOfWeek: null, time: null});
      if (type === "daily") append({time: null});
    }
  }, [fields, append, type]);

  if (!type) return null;

  // Weekly / monthly / annual: day/date is required (ON *). Daily: only time (AT *).
  const showDayColumn = type !== "daily";
  const timeRequired = type === "daily";
  // Weekly day buttons can use remaining width; monthly/annual stay inline next to AT.
  const dayColumnFlex = type === "weekly";

  return (
    <Schedule showDayColumn={showDayColumn} dayColumnFlex={dayColumnFlex}>
      {showDayColumn && (
        <DayColumn>
          <Label bold>
            ON&nbsp;<span>*</span>
          </Label>
        </DayColumn>
      )}
      <TimeColumn>
        <Label bold>
          AT
          {timeRequired && <span>&nbsp;*</span>}
        </Label>
      </TimeColumn>
      {/* Keeps AT in the same column as the time field (actions column shares track width). */}
      <Actions />

      {fields?.map((field, index) => {
        const dayOfMonth = watch(`${name}.occurrences.${index}.dayOfMonth`);

        const parsed = dayOfMonth ? parseInt(dayOfMonth, 10) : null;

        return (
          <Fragment key={field.id}>
            {type === "annual" && (
              <DayColumn>
                <InputDate name={`${name}.occurrences.${index}.dayOfYear`} />
              </DayColumn>
            )}

            {type === "monthly" && (
              <DayColumn>
                <InputSelect
                  name={`${name}.occurrences.${index}.dayOfMonth`}
                  options={daysOfMonth}
                  placeholder="Select day..."
                />
              </DayColumn>
            )}

            {type === "weekly" && (
              <DayColumn>
                <InputDay
                  name={`${name}.occurrences.${index}.dayOfWeek`}
                  align="row"
                  restrictOne
                />
              </DayColumn>
            )}

            <TimeColumn>
              <InputTime name={`${name}.occurrences.${index}.time`} />
            </TimeColumn>

            <Actions>
              <Button type="button" onClick={() => remove(index)} hide={fields.length === 1}>
                <FontAwesomeIcon icon={faMinus} />
              </Button>

              {options && options.length > 0 && fields.length < options.length && (
                <Button type="button" onClick={() => insert(index + 1, "")}>
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              )}
            </Actions>

            {!!parsed && parsed > 28 && (
              <MonthWarning>
                <Warning>Warning!</Warning>
                <Text quiet>
                  For months with less than {parsed} days, notification will be triggered on the
                  last day of the month.
                </Text>
              </MonthWarning>
            )}
          </Fragment>
        );
      })}
    </Schedule>
  );
};

InputNotification.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
};

// Style Overrides
// Single grid so ON/AT share column tracks with the fields (separate header/row grids mis-sized AT).
const Schedule = styled.div`
  display: grid;
  width: 100%;
  column-gap: ${pad}px;
  row-gap: ${pad / 2}px;
  align-items: end;
  grid-template-columns: ${({showDayColumn, dayColumnFlex}) => {
    if (!showDayColumn) return "120px max-content";
    if (dayColumnFlex) return "minmax(0, 1fr) 120px max-content";
    return "max-content 120px max-content";
  }};

  ${FormFieldWrapper} {
    margin-top: 0;
  }
`;

const DayColumn = styled.div`
  min-width: 0;
`;

const TimeColumn = styled.div`
  width: 120px;
`;

const Actions = styled(Inline)`
  align-items: center;
`;

const MonthWarning = styled(Inline)`
  grid-column: 1 / -1;
  margin: 0;
`;

export default InputNotification;
